/**
 * lib/cards/index.js — Card export facade.
 *
 * Single entry point that callers use regardless of template.
 * Dispatches to the right renderer based on tenant.card_template.
 *
 *   import { generateCardPdf } from '../lib/cards';
 *   const pdf = await generateCardPdf({ staff, tenant, staffSlug });
 *   pdf.save(`${staff.first_name}-${staff.last_name}.pdf`);
 *
 * Also re-exports the shared helpers that other pages need
 * (applyDisplayNameMode is used by the verify page; verifyUrl is
 * used wherever we embed the public URL).
 */

import { renderMinimalCard } from './template-minimal.js';
import { renderPremiumCard } from './template-premium.js';
import { renderCustomLayoutCard } from './render-custom-layout.js';

// Re-exports for consumers that don't need full card generation
export {
  applyDisplayNameMode,
  verifyUrl,
  generateQR,
  loadImageAsDataUrl,
  resolveBrandColor,
} from './shared.js';

/**
 * Valid template values. Must match the backend CHECK constraint on
 * tenants.card_template.
 */
export const TEMPLATES = ['minimal', 'premium'];

/**
 * Generate a printable PDF ID card for a single staff member.
 *
 * @param args
 * @param args.staff       { first_name, last_name, role, photo_url }
 * @param args.tenant      tenant record — see template files for
 *                         the full field list each template reads
 * @param args.staffSlug   slug for QR encoding
 * @returns Promise<jsPDF> call .save(filename) or .output('blob')
 */
export async function generateCardPdf({ staff, tenant, staffSlug }) {
  const rawLayout = tenant.card_layout_json;
  let layout = null;
  if (rawLayout) {
    if (typeof rawLayout === 'string') {
      try {
        layout = JSON.parse(rawLayout);
      } catch (e) {
        console.warn('[cards] card_layout_json parse failed, falling back to preset:', e);
      }
    } else if (typeof rawLayout === 'object') {
      layout = rawLayout;
    }
  }

  if (layout) {
    return renderCustomLayoutCard({ staff, tenant, staffSlug, layout });
  }

  if (tenant.card_template === 'premium') {
    return renderPremiumCard({ staff, tenant, staffSlug });
  }
  return renderMinimalCard({ staff, tenant, staffSlug });
}
